import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Dialog, QuestionDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { T } from 'src/shared/helpers';
import AxisIcon from './icons/axisIcon.svg';
import CollapseIcon from './icons/collapseIcon.svg';
import ExplodeIcon from './icons/explodeIcon.svg';
import GridIcon from './icons/gridIcon.svg';
import ResetCameraIcon from './icons/resetCameraIcon.svg';
import RotateIcon from './icons/rotateIcon.svg';
import ScaleIcon from './icons/scaleIcon.svg';
import SelectionIcon from './icons/selectionIcon.svg';
import TranslateIcon from './icons/translateIcon.svg';

// Container per i tool in alto a destra (orizzontali)
const TopRightToolbar = styled.div`
    position: absolute;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 8px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 1000;
`;

// Container per i tool a sinistra (verticali)
const LeftToolbar = styled.div`
    position: absolute;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 1000;
`;

// Stile base per i tool buttons
const ToolButton = styled.button<{ $isActive?: boolean; disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 6px;
    background: ${(props) => (props.$isActive ? '#2196f3' : 'transparent')};
    color: ${(props) => (props.$isActive ? '#fff' : '#333')};
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
        background: ${(props) => (props.$isActive ? '#1976d2' : '#f0f0f0')};
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        cursor: not-allowed;
        background: #e0e0e0;
        color: #b0b0b0;
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

// Container per il pannello explode aggiuntivo
const ExplodeAndCollapsePanel = styled.div<{ $isCollapse?: boolean }>`
    position: absolute;
    ${(props) => (props.$isCollapse ? 'top: 60%;' : 'top: 50%;')}
    right: 76px; /* 20px base + 56px larghezza toolbar */
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 1000;
    overflow: visible;
`;

// SVG Icons come componenti React
const SwitchIcon: React.FC = () => (
    <svg viewBox='0 0 24 24' fill='currentColor'>
        <path d='M17 7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h10c2.76 0 5-2.24 5-5s-2.24-5-5-5zM7 15c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z' />
    </svg>
);

const SaveIcon: React.FC = () => (
    <svg viewBox='0 0 24 24' fill='currentColor'>
        <path d='M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z' />
    </svg>
);

const RemoveIcon: React.FC = () => (
    <svg viewBox='0 0 24 24' fill='currentColor'>
        <path d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z' />
    </svg>
);

enum EditingMode {
    None = 'None',
    Translate = 'Translate',
    Rotate = 'Rotate',
    Scaling = 'Scaling'
}

enum EnvironmentMode {
    Editor = 1,
    Viewer = 2
}

interface SceneEditorToolbarProps {
    sceneEditor?: any; // Opzionale, se non disponibile dallo store
}

const SceneEditorToolbar: FC<SceneEditorToolbarProps> = ({ sceneEditor }) => {
    console.log('🔧 Rendering SceneEditorToolbar');
    if (!sceneEditor) {
        console.log('🚫 SceneEditor not available from store, skipping toolbar render');
        return null;
    }

    const [currentMode, setCurrentMode] = useState<EditingMode>(EditingMode.None);
    const [showGrid, setShowGrid] = useState(true);
    const [showAxes, setShowAxes] = useState(false);
    const [areGizmosVisible, setAreGizmosVisible] = useState(false);
    const [showSelection, setShowSelection] = useState(false);
    const sceneConfig = sceneEditor?.getSceneConfig?.();
    const cameraConfig = sceneConfig?.get?.('camera');
    const [autoRotate, setAutoRotate] = useState(cameraConfig ? cameraConfig.get('autoRotate') : false);
    const [environmentMode, setEnvironmentMode] = useState<EnvironmentMode>(EnvironmentMode.Editor);
    const [showExplodePanel, setShowExplodePanel] = useState(false);
    const [areExplodePositionsAvailable, setAreExplodePositionsAvailable] = useState(() => {
        // Inizializza con il valore reale al primo render - FIX logica
        const explodeLocation = sceneEditor?.zkScene?.getFirstExplodedMeshLocation?.();
        return explodeLocation !== undefined && explodeLocation !== null;
    });
    const [showCollapsePanel, setShowCollapsePanel] = useState(false);
    const [areCollapsePositionsAvailable, setAreCollapsePositionsAvailable] = useState(() => {
        // Inizializza con il valore reale al primo render - FIX logica
        const collapseLocation = sceneEditor?.zkScene?.getFirstCollapsedMeshLocation?.();
        return collapseLocation !== undefined && collapseLocation !== null;
    });
    const { showDialog, closeDialog } = useDialogManager();

    // Funzione per controllare la disponibilità delle posizioni
    const checkPositionsAvailability = useCallback(() => {
        if (sceneEditor?.zkScene) {
            const explodeLocation = sceneEditor.zkScene.getFirstExplodedMeshLocation?.();
            const collapseLocation = sceneEditor.zkScene.getFirstCollapsedMeshLocation?.();

            console.log('🔍 Checking positions:');
            console.log('  - Explode location:', explodeLocation);
            console.log('  - Collapse location:', collapseLocation);

            // FIX: Correzione della logica - deve essere AND, non OR
            const newExplodeAvailable = explodeLocation !== undefined && explodeLocation !== null;
            const newCollapseAvailable = collapseLocation !== undefined && collapseLocation !== null;

            console.log('  - Explode available:', newExplodeAvailable);
            console.log('  - Collapse available:', newCollapseAvailable);

            // Aggiorna solo se i valori sono effettivamente diversi
            setAreExplodePositionsAvailable((prev) => {
                if (prev !== newExplodeAvailable) {
                    console.log('🔄 Updating explode availability:', prev, '->', newExplodeAvailable);
                    return newExplodeAvailable;
                }
                return prev;
            });

            setAreCollapsePositionsAvailable((prev) => {
                if (prev !== newCollapseAvailable) {
                    console.log('🔄 Updating collapse availability:', prev, '->', newCollapseAvailable);
                    return newCollapseAvailable;
                }
                return prev;
            });
        }
    }, [sceneEditor]);

    // Inizializzazione
    useEffect(() => {
        if (sceneEditor && typeof sceneEditor.setEditingMode === 'function') {
            // Sincronizza lo stato iniziale
            const currentMode = sceneEditor.editingMode || EditingMode.None;
            setCurrentMode(currentMode);
            setShowGrid(sceneEditor.showGround || false);
            setShowAxes(sceneEditor.showAxes || false);
            if (sceneEditor.editControlVisible !== undefined) {
                setAreGizmosVisible(sceneEditor.editControlVisible);
            }
            // Controllo iniziale delle posizioni
            checkPositionsAvailability();
        }
    }, [sceneEditor, checkPositionsAvailability]);

    // Gizmo toggle helper
    const toggleGizmos = useCallback(
        (mode: EditingMode) => {
            if (currentMode === mode) {
                setAreGizmosVisible((visible) => {
                    const newVisible = !visible;
                    sceneEditor?.showHideEditControl(newVisible);
                    return newVisible;
                });
            } else {
                if (!areGizmosVisible) {
                    sceneEditor?.showHideEditControl(true);
                    setAreGizmosVisible(true);
                }
            }
        },
        [currentMode, areGizmosVisible, sceneEditor]
    );

    // Transform tools
    const handleTranslateClick = useCallback(() => {
        toggleGizmos(EditingMode.Translate);
        const newMode = EditingMode.Translate;
        setCurrentMode(newMode);
        sceneEditor?.setEditingMode?.(newMode);
    }, [toggleGizmos, sceneEditor]);

    const handleRotateClick = useCallback(() => {
        toggleGizmos(EditingMode.Rotate);
        const newMode = EditingMode.Rotate;
        setCurrentMode(newMode);
        sceneEditor?.setEditingMode?.(newMode);
    }, [toggleGizmos, sceneEditor]);

    const handleScaleClick = useCallback(() => {
        toggleGizmos(EditingMode.Scaling);
        const newMode = EditingMode.Scaling;
        setCurrentMode(newMode);
        sceneEditor?.setEditingMode?.(newMode);
    }, [toggleGizmos, sceneEditor]);

    // Other top-right tools
    const handleAxisClick = useCallback(() => {
        const newValue = !showAxes;
        setShowAxes(newValue);
        sceneEditor?.showHideAxes?.(newValue);
    }, [sceneEditor, showAxes]);

    const handleSelectionClick = useCallback(() => {
        const newMode = EditingMode.None;
        setCurrentMode(newMode);
        sceneEditor?.setEditingMode?.(newMode);
        sceneEditor.showHideHighlight(!showSelection);
        setShowSelection(!showSelection);
    }, [sceneEditor, showSelection]);

    const handleGridClick = useCallback(() => {
        const newValue = !showGrid;
        setShowGrid(newValue);
        if (sceneEditor) sceneEditor.showGround = newValue;
    }, [sceneEditor, showGrid]);

    // Left toolbar tools
    const handleResetCameraClick = useCallback(() => {
        const currentEnvMode = sceneEditor?.envMode || sceneEditor?.getEnvironmentMode?.() || environmentMode;
        const newEnvironmentMode = currentEnvMode === EnvironmentMode.Editor ? EnvironmentMode.Viewer : EnvironmentMode.Editor;

        sceneEditor?.setEnvironmentMode(newEnvironmentMode);
        setEnvironmentMode(newEnvironmentMode);

        if (newEnvironmentMode === EnvironmentMode.Viewer) {
            sceneEditor?.adjustCamera();
        }
    }, [sceneEditor, environmentMode]);

    const handleEnvironmentSwitchOnly = useCallback(() => {
        const currentEnvMode = sceneEditor?.envMode || sceneEditor?.getEnvironmentMode?.() || environmentMode;
        const newEnvironmentMode = currentEnvMode === EnvironmentMode.Editor ? EnvironmentMode.Viewer : EnvironmentMode.Editor;

        sceneEditor?.setEnvironmentMode?.(newEnvironmentMode);
        setEnvironmentMode(newEnvironmentMode);
    }, [sceneEditor, environmentMode]);

    const handleAutoRotateCameraClick = useCallback(() => {
        const newAutoRotate = !autoRotate;

        // Se autoRotate è true, imposta environment mode a Viewer, altrimenti Editor
        const newEnvironmentMode = newAutoRotate ? EnvironmentMode.Viewer : EnvironmentMode.Editor;

        sceneEditor?.setEnvironmentMode(newEnvironmentMode);
        sceneEditor?.setViewerCameraAutoRotate(newAutoRotate);

        setAutoRotate(newAutoRotate);
        setEnvironmentMode(newEnvironmentMode);
    }, [sceneEditor, autoRotate]);

    const handleExplodeClick = useCallback(() => {
        // Toggle del pannello explode
        setShowExplodePanel((prev) => !prev);
        // Chiudi il pannello collapse se aperto
        setShowCollapsePanel(false);
    }, []);

    const handleCollapseClick = useCallback(() => {
        // Toggle del pannello collapse
        setShowCollapsePanel((prev) => !prev);
        // Chiudi il pannello explode se aperto
        setShowExplodePanel(false);
    }, []);

    // Gestori per i pulsanti del pannello explode
    const handleSwitchExplode = useCallback(() => {
        sceneEditor.explodeMeshes();
    }, [sceneEditor]);

    const handleSaveExplode = useCallback(() => {
        const handleSaveConfirm = (id: string) => {
            closeDialog(id);
            console.log('💾 Saving explode position...');
            sceneEditor?.saveExplodedMeshLocation?.();

            // Aggiorna lo stato locale e poi forza un controllo
            setAreExplodePositionsAvailable(true);

            // Forza l'aggiornamento dello stato dopo il salvataggio
            setTimeout(() => {
                checkPositionsAvailability();
            }, 100);
        };

        showDialog(
            'save-explode-dialog',
            <QuestionDialog
                title={T._('Are you sure?', 'Admin')}
                message={T._('Do you want save the exploded positions?', 'Admin')}
                onYesClick={() => handleSaveConfirm('save-explode-dialog')}
                onNoClick={() => closeDialog('save-explode-dialog')}
            />
        );
    }, [sceneEditor, checkPositionsAvailability, showDialog, closeDialog]);

    const handleRemoveExplode = useCallback(() => {
        const handleDeleteConfirm = (id: string) => {
            closeDialog(id);
            console.log('🗑️ Removing explode positions');

            // Prima aggiorna lo stato locale
            setAreExplodePositionsAvailable(false);

            try {
                const location = sceneEditor.zkScene!.getFirstExplodedMeshLocation();
                if (location) {
                    sceneEditor.zkScene!.get('meshLocations').remove(location);
                    console.log('✅ Explode position removed successfully');

                    // Forza il refresh della scena se necessario
                    if (typeof sceneEditor.zkScene.refresh === 'function') {
                        sceneEditor.zkScene.refresh();
                    }
                }
            } catch (error) {
                console.error('❌ Error removing explode position:', error);
                // In caso di errore, ripristina lo stato
                checkPositionsAvailability();
            }
        };

        showDialog(
            'delete-explode-dialog',
            <QuestionDialog
                title={T._('Are you sure?', 'Admin')}
                message={T._('Do you want delete the exploded positions?', 'Admin')}
                onYesClick={() => handleDeleteConfirm('delete-explode-dialog')}
                onNoClick={() => closeDialog('delete-explode-dialog')}
            />
        );
    }, [sceneEditor, checkPositionsAvailability, showDialog, closeDialog]);

    const handleSwitchCollapse = useCallback(() => {
        sceneEditor.collapseMeshes();
    }, [sceneEditor]);

    const handleSaveCollapse = useCallback(() => {
        const handleSaveConfirm = (id: string) => {
            closeDialog(id);
            console.log('💾 Saving collapse position...');
            sceneEditor?.saveCollapsedMeshLocation?.();

            // Aggiorna lo stato locale e poi forza un controllo
            setAreCollapsePositionsAvailable(true);

            // Forza l'aggiornamento dello stato dopo il salvataggio
            setTimeout(() => {
                checkPositionsAvailability();
            }, 100);
        };

        showDialog(
            'save-collapse-dialog',
            <Dialog onClose={() => closeDialog('save-collapse-dialog')}>
                <QuestionDialog
                    title={T._('Are you sure?', 'Admin')}
                    message={T._('Do you want save the collapsed positions?', 'Admin')}
                    onYesClick={() => handleSaveConfirm('save-collapse-dialog')}
                    onNoClick={() => closeDialog('save-collapse-dialog')}
                />
            </Dialog>
        );
    }, [sceneEditor, checkPositionsAvailability, showDialog, closeDialog]);

    const handleRemoveCollapse = useCallback(() => {
        const handleDeleteConfirm = (id: string) => {
            closeDialog(id);
            console.log('🗑️ Removing collapse positions');

            // Prima aggiorna lo stato locale
            setAreCollapsePositionsAvailable(false);

            try {
                const location = sceneEditor.zkScene!.getFirstCollapsedMeshLocation();
                if (location) {
                    sceneEditor.zkScene!.get('meshLocations').remove(location);
                    console.log('✅ Collapse position removed successfully');

                    // Forza il refresh della scena se necessario
                    if (typeof sceneEditor.zkScene.refresh === 'function') {
                        sceneEditor.zkScene.refresh();
                    }
                }
            } catch (error) {
                console.error('❌ Error removing collapse position:', error);
                // In caso di errore, ripristina lo stato
                checkPositionsAvailability();
            }
        };

        showDialog(
            'delete-collapse-dialog',
            <Dialog onClose={() => closeDialog('delete-collapse-dialog')}>
                <QuestionDialog
                    title={T._('Are you sure?', 'Admin')}
                    message={T._('Do you want delete the collapsed positions?', 'Admin')}
                    onYesClick={() => handleDeleteConfirm('delete-collapse-dialog')}
                    onNoClick={() => closeDialog('delete-collapse-dialog')}
                />
            </Dialog>
        );
    }, [sceneEditor, checkPositionsAvailability, showDialog, closeDialog]);

    return (
        <>
            <TopRightToolbar>
                <ZTooltip title='Translate' position='bottom'>
                    <ToolButton $isActive={currentMode === EditingMode.Translate} onClick={handleTranslateClick}>
                        <TranslateIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Rotate' position='bottom'>
                    <ToolButton $isActive={currentMode === EditingMode.Rotate} onClick={handleRotateClick}>
                        <RotateIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Scale' position='bottom'>
                    <ToolButton $isActive={currentMode === EditingMode.Scaling} onClick={handleScaleClick}>
                        <ScaleIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Show/Hide Axes' position='bottom'>
                    <ToolButton $isActive={showAxes} onClick={handleAxisClick}>
                        <AxisIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Selection Mode' position='bottom'>
                    <ToolButton $isActive={showSelection} onClick={handleSelectionClick}>
                        <SelectionIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Show/Hide Grid' position='bottom'>
                    <ToolButton $isActive={showGrid} onClick={handleGridClick}>
                        <GridIcon />
                    </ToolButton>
                </ZTooltip>
            </TopRightToolbar>

            <LeftToolbar>
                <ZTooltip title='Switch Environment' position='left'>
                    <ToolButton onClick={handleEnvironmentSwitchOnly} $isActive={environmentMode === EnvironmentMode.Viewer}>
                        <SwitchIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Reset Camera' position='left'>
                    <ToolButton onClick={handleResetCameraClick}>
                        <ResetCameraIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Auto rotate' position='left'>
                    <ToolButton onClick={handleAutoRotateCameraClick}>
                        <ResetCameraIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Explode Tools' position='left'>
                    <ToolButton $isActive={showExplodePanel} onClick={handleExplodeClick}>
                        <ExplodeIcon />
                    </ToolButton>
                </ZTooltip>

                <ZTooltip title='Collapse Tools' position='left'>
                    <ToolButton $isActive={showCollapsePanel} onClick={handleCollapseClick}>
                        <CollapseIcon />
                    </ToolButton>
                </ZTooltip>
            </LeftToolbar>

            {showExplodePanel && (
                <ExplodeAndCollapsePanel>
                    <ZTooltip title='Explode' position='left'>
                        <ToolButton disabled={!areExplodePositionsAvailable} onClick={handleSwitchExplode}>
                            <SwitchIcon />
                        </ToolButton>
                    </ZTooltip>

                    <ZTooltip title='Save Exploded Position' position='left'>
                        <ToolButton
                            // SAVE è sempre attivo - puoi sempre salvare una posizione
                            onClick={handleSaveExplode}
                        >
                            <SaveIcon />
                        </ToolButton>
                    </ZTooltip>

                    <ZTooltip title='Reset Exploded Positions' position='left'>
                        <ToolButton disabled={!areExplodePositionsAvailable} onClick={handleRemoveExplode}>
                            <RemoveIcon />
                        </ToolButton>
                    </ZTooltip>
                </ExplodeAndCollapsePanel>
            )}

            {showCollapsePanel && (
                <ExplodeAndCollapsePanel $isCollapse>
                    <ZTooltip title='Collapse' position='left'>
                        <ToolButton disabled={!areCollapsePositionsAvailable} onClick={handleSwitchCollapse}>
                            <SwitchIcon />
                        </ToolButton>
                    </ZTooltip>

                    <ZTooltip title='Save Collapsed Position' position='left'>
                        <ToolButton
                            // SAVE è sempre attivo - puoi sempre salvare una posizione
                            onClick={handleSaveCollapse}
                        >
                            <SaveIcon />
                        </ToolButton>
                    </ZTooltip>

                    <ZTooltip title='Reset Collapsed Positions' position='left'>
                        <ToolButton disabled={!areCollapsePositionsAvailable} onClick={handleRemoveCollapse}>
                            <RemoveIcon />
                        </ToolButton>
                    </ZTooltip>
                </ExplodeAndCollapsePanel>
            )}
        </>
    );
};

export default SceneEditorToolbar;
